import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useMerchant } from '../context/MerchantContext';
import { customerApi, zipCodeApi } from '../services/api';
import { AddressDto } from '../types';
import ZipCodeFormModal from './ZipCodeFormModal';
import AddressFormModal from './AddressFormModal';
import AddressAutocomplete from './AddressAutocomplete';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import {
  ModalHeader,
  ModalTitle,
  CloseButton,
  BackButton,
  ModalBody
} from './common/modal/ModalComponents';
import Modal from './common/modal/Modal';

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const AddressItem = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1rem;
  border: ${props => props.active ? `2px solid ${props.theme.primary || '#e74c3c'}` : '1px solid #e2e8f0'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #fff;

  &:hover {
    border-color: ${props => props.theme.primary || '#e74c3c'};
    background-color: #fcfcfc;
  }

  i {
    color: ${props => props.theme.primary || '#e74c3c'};
    font-size: 1.5rem;
    background-color: ${props => props.theme.primary ? `${props.theme.primary}1A` : '#fff5f5'};
    padding: 0.75rem;
    border-radius: 10px;
  }
`;

const AddressDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  strong {
    font-size: 1.1rem;
    color: #2d3748;
    font-weight: 700;
  }

  span {
    font-size: 0.95rem;
    color: #718096;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #a0aec0;
  font-size: 0.85rem;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #e2e8f0;
  }

  span {
    padding: 0 0.75rem;
  }
`;

const NewAddressButton = styled.button`
  width: 100%;
  padding: 1rem;
  background-color: transparent;
  color: ${props => props.theme.primary || '#e74c3c'};
  border: 1px dashed ${props => props.theme.primary || '#e74c3c'};
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.primary ? `${props.theme.primary}1A` : '#fff5f5'};
  }
`;

interface AddressSelectionModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSelect?: (address: AddressDto) => void;
  selectedAddress?: AddressDto | null;
}

const AddressSelectionModal: React.FC<AddressSelectionModalProps> = ({
  isOpen: propIsOpen,
  onClose: propOnClose,
  onSelect: propOnSelect,
  selectedAddress: propSelectedAddress
}) => {
  const { colors: themeColors } = useTheme();
  const { isAuthenticated } = useAuth();
  const { 
    showAddressModal, 
    setShowAddressModal, 
    selectedAddress: contextSelectedAddress, 
    setSelectedAddress 
  } = useMerchant();

  const isOpen = propIsOpen !== undefined ? propIsOpen : showAddressModal;
  const selectedAddress = propSelectedAddress !== undefined ? propSelectedAddress : contextSelectedAddress;

  const [userAddresses, setUserAddresses] = useState<AddressDto[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [isLoadingZipCode, setIsLoadingZipCode] = useState(false);
  const [zipCodeError, setZipCodeError] = useState<string | null>(null);
  const [showZipCodeSearch, setShowZipCodeSearch] = useState(false);
  const [isAddressFormModalOpen, setIsAddressFormModalOpen] = useState(false);
  const [addressForm, setAddressForm] = useState<AddressDto>({
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    complement: ''
  });

  const handleClose = () => {
    if (propOnClose) {
      propOnClose();
    } else {
      setShowAddressModal(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (isAuthenticated) {
        loadAddresses();
      } else {
        const storedAddress = localStorage.getItem('guestAddress');
        if (storedAddress) {
          try {
            const parsed = JSON.parse(storedAddress);
            setUserAddresses([parsed]);
            setShowNewAddressForm(false);
          } catch (e) {
            setShowNewAddressForm(true);
          }
        } else {
          setShowNewAddressForm(true);
        }
      }
    } else {
      // Reset state when modal is closed
      setIsAddressFormModalOpen(false);
      setZipCode('');
      setZipCodeError(null);
      setShowZipCodeSearch(false);
    }
  }, [isOpen, isAuthenticated]);

  const loadAddresses = async () => {
    try {
      setIsLoadingAddresses(true);
      const data = await customerApi.listAddresses();
      setUserAddresses(data);
      
      // Se tiver endereços ou se houver um endereço selecionado (mesmo que seja temporário)
      if (data.length === 0 && !selectedAddress) {
        setShowNewAddressForm(true);
      } else {
        setShowNewAddressForm(false);
      }
    } catch (err) {
      console.error('Error loading addresses:', err);
      if (!selectedAddress) {
        setShowNewAddressForm(true);
      }
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const handleSelectExistingAddress = (address: AddressDto) => {
    setAddressForm({
      id: address.id,
      street: address.street || '',
      number: address.number || '',
      neighborhood: address.neighborhood || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zipCode || '',
      complement: address.complement || '',
      nickname: address.nickname || ''
    });
    setIsAddressFormModalOpen(true);
  };

  const handleAddressAutocomplete = (addr: AddressDto) => {
    setAddressForm({
      street: addr.street || '',
      number: addr.number || '',
      neighborhood: addr.neighborhood || '',
      city: addr.city || '',
      state: addr.state || '',
      zipCode: addr.zipCode || '',
      complement: '',
      latitude: addr.latitude,
      longitude: addr.longitude,
    });
    setIsAddressFormModalOpen(true);
  };

  const handleZipCodeSubmit = async () => {
    setIsLoadingZipCode(true);
    setZipCodeError(null);
    try {
      const data = await zipCodeApi.getAddressByZipCode(zipCode);
      setAddressForm({
        street: data.logradouro || '',
        number: '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        zipCode: data.cep || zipCode,
        complement: data.complemento || ''
      });
      setIsAddressFormModalOpen(true);
    } catch (error) {
      setZipCodeError('CEP não encontrado.');
    } finally {
      setIsLoadingZipCode(false);
    }
  };

  const handleAddressSubmit = async () => {
    let finalAddress = { ...addressForm };
    
    // Ensure all mandatory fields are present
    if (!finalAddress.street || !finalAddress.number || !finalAddress.neighborhood || !finalAddress.city || !finalAddress.state || !finalAddress.zipCode) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (isAuthenticated) {
      try {
        if (finalAddress.id) {
          await customerApi.updateAddress(finalAddress.id, finalAddress);
          toast.success('Endereço atualizado com sucesso!');
        } else {
          const savedAddress = await customerApi.addAddress(addressForm);
          finalAddress = savedAddress;
          toast.success('Endereço salvo com sucesso!');
        }
      } catch (error) {
        console.error('Error saving address:', error);
        toast.error('Endereço selecionado, mas houve um erro ao salvar em sua conta.');
      }
    } else {
      // For guest, store in localStorage
      localStorage.setItem('guestAddress', JSON.stringify(finalAddress));
    }
    
    if (propOnSelect) {
      propOnSelect(finalAddress);
    } else {
      setSelectedAddress(finalAddress);
    }
    
    setIsAddressFormModalOpen(false);
    handleClose();
  };

  const handleAddressChange = (field: string, value: string) => {
    setAddressForm(prev => ({ ...prev, [field as keyof AddressDto]: value }));
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} zIndex={10500}>
        <ModalHeader sticky>
          {isAddressFormModalOpen ? (
            <BackButton onClick={() => {
              setIsAddressFormModalOpen(false);
              setShowNewAddressForm(true);
            }}>
              ‹
            </BackButton>
          ) : (
            <div style={{ width: '32px' }} />
          )}
          <ModalTitle>
            {isAddressFormModalOpen ? 'Complete seu endereço' : 'Endereço de entrega'}
          </ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {isAddressFormModalOpen ? (
            <AddressFormModal
              address={addressForm as any}
              onAddressChange={handleAddressChange}
              onSubmit={handleAddressSubmit}
            />
          ) : (
            <>
              {(userAddresses.length > 0) && (
                <>
                  <p style={{ fontSize: '1rem', color: '#4a5568', marginBottom: '1.5rem', fontWeight: 600 }}>
                    Escolha um endereço salvo ou digite um novo:
                  </p>
                  <AddressList>
                    {userAddresses.map((addr) => {
                      const addrId = addr.id || `${addr.zipCode}-${addr.number}`;
                      const isSelected = selectedAddress && (selectedAddress.id === addr.id || (selectedAddress.zipCode === addr.zipCode && selectedAddress.number === addr.number));
                      return (
                        <AddressItem 
                          key={addrId} 
                          active={!!isSelected}
                          onClick={() => handleSelectExistingAddress(addr)}
                        >
                          <i className={`fas ${addr.nickname?.toLowerCase() === 'casa' ? 'fa-home' : addr.nickname?.toLowerCase() === 'trabalho' ? 'fa-briefcase' : 'fa-map-marker-alt'}`}></i>
                          <AddressDetails>
                            <strong>{addr.nickname || addr.street}</strong>
                            <span>{addr.street}, {addr.number}</span>
                          </AddressDetails>
                          {isSelected && (
                            <div style={{ 
                              marginLeft: 'auto', 
                              width: '24px', 
                              height: '24px', 
                              borderRadius: '50%', 
                              backgroundColor: themeColors.primary || '#e74c3c', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              color: 'white'
                            }}>
                              <i className="fas fa-check" style={{ fontSize: '0.8rem', color: 'white', padding: 0, backgroundColor: 'transparent' }}></i>
                            </div>
                          )}
                        </AddressItem>
                      );
                    })}
                  </AddressList>
                  
                  {!showNewAddressForm && (
                    <NewAddressButton onClick={() => setShowNewAddressForm(true)}>
                      <i className="fas fa-plus"></i>
                      Usar outro endereço
                    </NewAddressButton>
                  )}
                  
                  {showNewAddressForm && <OrDivider><span>OU</span></OrDivider>}
                </>
              )}

              {isLoadingAddresses && isAuthenticated && userAddresses.length === 0 && (
                <div style={{ textAlign: 'center', padding: '1rem' }}>Carregando endereços...</div>
              )}

              {(showNewAddressForm || (userAddresses.length === 0 && !isLoadingAddresses)) && (
                <>
                  <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                    Busque pelo seu endereço
                  </p>
                  <AddressAutocomplete
                    label="Endereço"
                    placeholder="Digite rua, número e cidade"
                    onSelect={handleAddressAutocomplete}
                  />

                  {!showZipCodeSearch ? (
                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                      <button
                        onClick={() => setShowZipCodeSearch(true)}
                        style={{ background: 'none', border: 'none', color: themeColors.primary || '#e74c3c', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Buscar pelo seu CEP
                      </button>
                    </div>
                  ) : (
                    <>
                      <OrDivider><span>OU</span></OrDivider>

                      <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>
                        Informe um CEP para entrega
                      </p>
                      <ZipCodeFormModal
                        zipCode={zipCode}
                        setZipCode={setZipCode}
                        onSubmit={handleZipCodeSubmit}
                        isLoading={isLoadingZipCode}
                        error={zipCodeError}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </ModalBody>
    </Modal>
  );
};

export default AddressSelectionModal;
